import { FC, MouseEvent } from 'react';

import useLockedBody from '@hooks/useLockedBody';
import CustomIcon from '@components/utils/CustomIcon';
import { CloseLine, PersonIcon } from '@assets/icons';
import { useAppRouter } from '@contexts/router';
import { useAuthState, useClearDispatch } from '@contexts/auth/AuthContext';
import { removeBasketDetailsOnLocalStorage } from '@utils/removeBasketDetailsOnLocalStorage';
import { LogoutCircle } from '@assets/icons/topHeader';
import STATIC_ROUTES from '@data/staticRoutes.json';

import {
  StyledDrawer,
  UserBadge,
  UserBadgeWrapper,
  UserFullName,
  UserMenuLink,
} from './UserMenuDrawerStyle';
import { USER_MENU_ITEMS } from '../../constants';

interface IUserMenuDrawer {
  isVisible: boolean;
  onClose: () => void;
}

const UserMenuDrawer: FC<IUserMenuDrawer> = ({
  isVisible = false,
  onClose,
}) => {
  useLockedBody(isVisible);
  const {
    userInfo: { id, firstName, lastName },
  } = useAuthState();
  const { push } = useAppRouter();
  const clearUserInformation = useClearDispatch();

  /**
   * Handle logout method
   * @param e MouseEvent<HTMLAnchorElement>
   * @returns boolean
   */
  const handleLogout = async (e: MouseEvent<HTMLAnchorElement>) => {
    /**
     * Check if logout link is clicked
     */
    if (e.currentTarget.href.includes('/logout')) {
      e.preventDefault();

      /**
       * Clear user information from context
       */
      clearUserInformation();

      /**
       * Remove basket details from local storage when user logs out
       */
      removeBasketDetailsOnLocalStorage();

      /**
       * Close user menu drawer
       */
      onClose();

      /**
       * Redirect to home page
       */
      push('/');
      return false;
    }

    onClose();
    return true;
  };

  return (
    <StyledDrawer
      onClose={onClose}
      open={isVisible}
      destroyOnHidden={true}
      styles={{
        wrapper: {
          height: '100%',
          width: '100%',
        },
      }}
      title={
        firstName && lastName ? (
          <UserBadgeWrapper>
            <UserBadge>
              {firstName?.slice(0, 1)}
              {lastName?.slice(0, 1)}
            </UserBadge>
            <UserFullName>
              {firstName} {lastName}
            </UserFullName>
          </UserBadgeWrapper>
        ) : null
      }
      closeIcon={
        <CustomIcon
          icon={CloseLine}
          fontSize={24}
          paletteColor='whiteAlpha600'
        />
      }
    >
      {USER_MENU_ITEMS.map((menuItem) => (
        <UserMenuLink
          key={menuItem?.id}
          href={menuItem?.href}
          onClick={onClose}
          prefetch={false}
        >
          <CustomIcon
            icon={menuItem?.icon}
            fontSize={20}
            paletteColor='grey600'
          />
          {menuItem?.name}
        </UserMenuLink>
      ))}

      {id ? (
        <UserMenuLink
          key='login-action'
          href='/logout'
          onClick={handleLogout}
          prefetch={false}
        >
          <CustomIcon
            icon={LogoutCircle}
            fontSize={20}
            paletteColor='grey600'
          />
          Çıkış Yap
        </UserMenuLink>
      ) : (
        <UserMenuLink
          key='login-action'
          href={STATIC_ROUTES.login}
          onClick={handleLogout}
          prefetch={false}
        >
          <CustomIcon icon={PersonIcon} fontSize={20} paletteColor='grey600' />
          Giriş Yap
        </UserMenuLink>
      )}
    </StyledDrawer>
  );
};

export default UserMenuDrawer;
